import { useContext } from 'react';

import {
  EmployeeTypeResponse,
  PlanningEntityIdentifier,
  PlanningEntityType,
  TransportationMode
} from 'generated';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';

import { Event } from '../../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';

import { BLUE_SECONDARY, ORANGE, PRIMARY, WHITE } from 'constants/colors';

import { shouldShowPopoverDialogAtom } from '../../atoms';

import { Box, Skeleton, SxProps } from '@mui/material';

import PopoverWarningDialog from '../../components/PopoverWarningDialog/PopoverWarningDialog';

import { RouteContextMenu } from '../../components';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import { formatByLocale } from 'utils/date';
import { useDroppable } from '@dnd-kit/core';
import { brightness } from 'utils/misc';

const StyledStickyHeader = styled(Box)`
  z-index: 35;
  position: sticky;
  top: 5px;
  padding: 0 10px;
  padding-right: 4px;
  display: flex;
  align-items: center;
`;

const IconsSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const EmployeeTypeName = styled(Box)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 112px;
`;

const TimeframeChip = ({
  showChip = false,
  label = '',
  sx = {}
}: {
  showChip: boolean | undefined;
  label?: string | null;
  sx?: SxProps;
}) => {
  if (!showChip) return <>{label}</>;
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        height: '15px',
        padding: '1px 5px',
        borderRadius: '20px',
        color: WHITE,
        backgroundColor: ORANGE,
        fontFamily: 'signikalight',
        ...sx
      }}
    >
      {label}
    </Box>
  );
};

type StickyHeaderProps = {
  id: string;
  droppable?: {
    droppableId?: string;
    planningEntityType: PlanningEntityType;
    planningEntityId: string;
    planningEntityIdentifier?: PlanningEntityIdentifier;
    scheduleStart: string;
  };
  name?: string;
  end: string;
  employeeId?: number;
  employeeType?: EmployeeTypeResponse;
  onConnectClick?: () => void;
  onDialogOpen?: (isOpen: boolean) => void;
  onContextMenu?: (open: boolean) => void;
  events?: Event[];
  readOnly?: boolean;
  start: string;
  date: string;
  showTimePeriod?: boolean;
  transportationMode?: TransportationMode;
  startsTooEarly?: boolean;
  endsTooLate?: boolean;
  isLoading?: boolean;
  isAssignableToEmployee?: boolean;
};

export default function StickyHeader({
  id,
  droppable,
  name,
  employeeId,
  employeeType,
  onConnectClick,
  onContextMenu,
  onDialogOpen,
  events,
  readOnly,
  transportationMode,
  date,
  start,
  end,
  startsTooEarly,
  endsTooLate,
  isLoading,
  isAssignableToEmployee
}: StickyHeaderProps) {
  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );
  const {
    isLoading: isLoadingConfirmation,
    onClose,
    onHandleAppointmentConfirmation
  } = useContext(PopOverConfirmationContext);

  const {
    droppableId,
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    scheduleStart
  } = droppable || {
    id: '',
    planningEntityType: '',
    planningEntityId: '',
    planningEntityIdentifier: {},
    scheduleStart: ''
  };

  const disabled: boolean = !droppable || !droppable.droppableId;
  const isSnapshot = !start && !end;

  const { setNodeRef, isOver } = useDroppable({
    id: droppableId || '',
    disabled,
    data: {
      planningEntityType,
      planningEntityId,
      planningEntityIdentifier,
      scheduleStart,
      isDroppablePanelHeader: true
    }
  });

  const isRosterEmployeePerspective =
    planningEntityType === 'EMPLOYEE' || planningEntityType === 'EMPLOYMENT';
  if (!readOnly && isLoading) {
    return (
      <>
        <Skeleton
          variant="rounded"
          width="100%"
          height="40px"
          sx={{
            minHeight: '40px',
            borderRadius: '6px',
            marginBottom: isSnapshot ? '8px' : 'initial',
            ...(isRosterEmployeePerspective && {
              borderRadius: '6px 6px 0 0',
              borderBottom: `1px solid rgba(0,0,0,0.05)`
            })
          }}
        />
        {!readOnly &&
          (planningEntityType === 'ROUTE' || isRosterEmployeePerspective) && (
            <PopoverWarningDialog
              open={shouldShowPopoverDialog.id == id && !isLoadingConfirmation}
              isMultipleSelected
              onClose={() => {
                onClose && onClose();
                setShouldShowPopoverDialog({ id: '' });
              }}
              onClickAppointment={(
                editAppointmentType: EditAppointmentType
              ) => {
                onHandleAppointmentConfirmation &&
                  onHandleAppointmentConfirmation(editAppointmentType);
                setShouldShowPopoverDialog({ id: '' });
              }}
            >
              <Box />
            </PopoverWarningDialog>
          )}
      </>
    );
  }

  return (
    <StyledStickyHeader
      ref={setNodeRef}
      sx={{
        minHeight: '40px',
        boxSizing: 'border-box',
        color: isOver ? PRIMARY : 'inherit',
        border: `2px solid ${isOver ? PRIMARY : 'transparent'}`,
        backgroundColor: isOver ? brightness(PRIMARY, 90) : BLUE_SECONDARY,
        borderRadius: '6px',
        marginBottom: isSnapshot ? '8px' : 'initial',
        ...(isRosterEmployeePerspective && {
          borderRadius: '6px 6px 0 0',
          ...(!isOver && {
            borderBottom: `1px solid rgba(0,0,0,0.05)`
          })
        })
      }}
    >
      <IconsSection>
        <Box display="flex" alignItems="center">
          <TimeframeChip
            showChip={startsTooEarly}
            label={start && formatByLocale(new Date(start), 'HH:mm')}
          />
          {start && end && <Box m="0 3px">-</Box>}
          <TimeframeChip
            showChip={endsTooLate}
            label={end && formatByLocale(new Date(end), 'HH:mm')}
          />
        </Box>
        {employeeType && (
          <EmployeeTypeName>{employeeType.name}</EmployeeTypeName>
        )}
      </IconsSection>

      <Box
        sx={{
          fontSize: 14,
          fontFamily: 'signikalight',
          marginLeft: '10px'
        }}
      >
        {name}
      </Box>
      <Box
        display="flex"
        gap={1}
        sx={{
          position: 'absolute',
          right: 0
        }}
      >
        {!readOnly && planningEntityType === 'ROUTE' && (
          <PopoverWarningDialog
            open={id === shouldShowPopoverDialog.id && !isLoadingConfirmation}
            isMultipleSelected
            onClose={() => {
              onClose && onClose();
              setShouldShowPopoverDialog({ id: '' });
            }}
            onClickAppointment={(editAppointmentType: EditAppointmentType) => {
              onHandleAppointmentConfirmation &&
                onHandleAppointmentConfirmation(editAppointmentType);
              setShouldShowPopoverDialog({ id: '' });
            }}
          >
            <Box>
              <RouteContextMenu
                appointmentGroupingEntityId={id}
                transportationMode={transportationMode}
                onConnectClick={onConnectClick}
                onDialogOpen={onDialogOpen}
                appointmentGroupingStart={date}
                appointmentGroupingName={name}
                events={events}
                employeeId={employeeId}
                onOpen={(open: boolean) => onContextMenu?.(open)}
                isAssignableToEmployee={!!isAssignableToEmployee}
                scheduleId={planningEntityId}
              />
            </Box>
          </PopoverWarningDialog>
        )}
      </Box>
    </StyledStickyHeader>
  );
}
